import { generateNanoId } from '@amzn/journeys-common';
import { flattenObject } from 'es-toolkit';
import { getImageDimensionsFromBase64 } from 'features/assets/assets.util';
import type { Rect } from 'features/assets/assets.util.types';
import type { BulkCreateAssetInput } from 'generated/graphql';

/**
 * Metadata for an asset being uploaded from a base64 string
 *
 * @typedef {Object} AssetUploadMeta
 * @property {File} file - File object created from the base64 string
 * @property {string} uploadTrackingId - Unique ID used to track this asset upload
 * @property {string} base64AssetString - Original base64 encoded image string
 * @property {string[]} contentPaths - Paths in the content object where this asset is used
 * @property {Rect} dimensions - Width and height of the image
 * @property {BulkCreateAssetInput} createAssetMutationInput - Input for the asset creation mutation
 */
export type AssetUploadMeta = {
  file: File;
  uploadTrackingId: string;
  base64AssetString: string;
  contentPaths: string[];
  dimensions: Rect;
  createAssetMutationInput: BulkCreateAssetInput;
};

/**
 * Regular expression to match base64 encoded images
 * Validates the format: data:image/[type];base64,[base64-data]
 */
const BASE64_IMAGE_REGEX = /^data:image\/[a-zA-Z]*;base64,([A-Za-z0-9+/=]+)$/;

/**
 * Checks if a string is a valid base64 encoded image
 *
 * @param {string} base64AssetString - String to check
 * @returns {boolean} True if the string is a valid base64 image
 */
const isBase64Image = (base64AssetString: string): boolean => {
  // Check for data URL format
  return BASE64_IMAGE_REGEX.test(base64AssetString);
};

/**
 * Finds Base64 encoded images within a JSON object
 * Flattens the object and searches for base64 image strings in all properties
 * Groups duplicate images by their base64 content to avoid multiple uploads
 *
 * @param {object} content - The object to search for base64 images
 * @returns {Array<{contentPaths: string[], base64AssetString: string}>} Array of objects containing base64 images and their paths
 */
const extractAssetBlobAndPathsFromObject = (
  content: object,
): Array<{
  contentPaths: string[];
  base64AssetString: string;
}> => {
  const flattenedContent = flattenObject(content);

  // Use a Map to track unique assets by their blob data (key: assetBlob, value: pathsToAsset)
  const assetMap = new Map<string, string[]>();

  // Iterate through all flattened key-value pairs
  for (const [path, value] of Object.entries(flattenedContent)) {
    // Check if the value is a string and could be a base64 image
    if (typeof value === 'string' && value.length > 0) {
      if (isBase64Image(value)) {
        // If this asset already exists in our map, add this path to its paths array
        if (assetMap.has(value)) {
          assetMap.get(value)?.push(path);
        } else {
          // Otherwise create a new entry with this path
          assetMap.set(value, [
            path,
          ]);
        }
      }
    }
  }
  const mapEntries = Array.from(assetMap.entries());

  return mapEntries.map(([base64AssetString, contentPaths]) => ({
    base64AssetString,
    contentPaths,
  }));
};

/**
 * Converts a base64 image string to a File object
 *
 * @param {string} base64AssetString - Base64 encoded image string
 * @param {string} fileNameBase - Base filename to use for the created file
 * @returns {Promise<File>} Promise that resolves to a File object
 * @throws {Error} If the base64 string is not a valid image format
 */
const convertBase64BlobToFileAndInfo = async (base64AssetString: string, fileNameBase: string): Promise<File> => {
  if (isBase64Image(base64AssetString)) {
    const contentType = base64AssetString.split(';')[0].split(':')[1];
    const extension = contentType.split('/')[1];
    const base64Data = base64AssetString.split(',')[1];
    const fileNameWithCorrectExtension = fileNameBase.includes('.') ? fileNameBase : `${fileNameBase}.${extension}`;

    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, {
      type: contentType,
    });

    const file = new File(
      [
        blob,
      ],
      fileNameWithCorrectExtension,
      {
        type: contentType,
      },
    );

    return file;
  }

  throw new Error('Invalid base64 image format');
};

/**
 * Creates metadata for an asset upload from a base64 image and its content paths
 *
 * @param {Object} params - Parameters
 * @param {string} params.base64AssetString - Base64 encoded image string
 * @param {string[]} params.contentPaths - Paths in the content object where the image is used
 * @param {string} params.projectId - ID of the project to associate with the asset
 * @returns {Promise<AssetUploadMeta>} Metadata for the asset upload
 */
const getAssetUploadMetaForBlobAndPaths = async ({
  base64AssetString,
  contentPaths,
  projectId,
}: {
  contentPaths: string[];
  base64AssetString: string;
  projectId: string;
}): Promise<AssetUploadMeta> => {
  const uploadTrackingId = `auto_upload_image_${generateNanoId()}`;
  const dimensions = await getImageDimensionsFromBase64(base64AssetString);
  const file = await convertBase64BlobToFileAndInfo(base64AssetString, uploadTrackingId);
  const createAssetMutationInput: BulkCreateAssetInput = {
    device: [],
    id: uploadTrackingId,
    mediaType: file.type,
    metadata: {
      height: dimensions.height,
      width: dimensions.width,
    },
    name: file.name,
    originalFileName: file.name,
    productLine: [],
    projectId,
    size: file.size,
    tags: [],
  };

  return {
    base64AssetString,
    contentPaths,
    createAssetMutationInput,
    dimensions,
    file,
    uploadTrackingId,
  };
};

/**
 * Extracts all base64 images from a content object and creates asset upload metadata
 *
 * This function:
 * 1. Finds all base64 images in the content object
 * 2. Converts them to File objects
 * 3. Creates asset upload metadata for each image
 * 4. Groups duplicate images to avoid multiple uploads
 *
 * @param {Object} params - Parameters
 * @param {object} params.someContent - Content object to extract images from
 * @param {string} params.projectId - Project ID to associate with the assets
 * @returns {Promise<AssetUploadMeta[]>} Array of asset upload metadata
 */
export const getBulkUploadAssetsMetaArray = async ({
  someContent,
  projectId,
}: {
  someContent: object;
  projectId: string;
}): Promise<AssetUploadMeta[]> => {
  const blobsAndPaths = extractAssetBlobAndPathsFromObject(someContent);

  if (blobsAndPaths.length === 0) {
    return [];
  }

  const newAssetUploadMetaArray: AssetUploadMeta[] = await Promise.all(
    blobsAndPaths.map(async ({ contentPaths, base64AssetString }) =>
      getAssetUploadMetaForBlobAndPaths({
        base64AssetString,
        contentPaths,
        projectId,
      }),
    ),
  );
  return newAssetUploadMetaArray;
};
